import { stationInput } from '@trafiku/contracts';
import { Check, TriangleAlert } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { FormActions } from '@/components/form-actions';
import { arrowIcon, drawStation, iconUrl } from '@/components/map/icons';
import { arrowLayer, fc, GeoJsonLayer, labelLayer, lineLayer } from '@/components/map/layers';
import { MapView } from '@/components/map/map-view';
import { OverviewMap } from '@/components/map/overview-map';
import { PointPicker } from '@/components/map/point-picker';
import { StationsLayer } from '@/components/map/stations-layer';
import { StationArrivals, useStationInfo } from '@/components/map-panels';
import { ReadOnlyNotice } from '@/components/read-only-notice';
import { ResourceList } from '@/components/resource-list';
import { Combobox, fold } from '@/components/ui/combobox';
import { Field, Input, Textarea } from '@/components/ui/form';
import { Card, ColorDot, PageHeader, Spinner } from '@/components/ui/layout';
import { useAuth } from '@/lib/auth';
import { safeHex, textOn } from '@/lib/colors';
import { useFormState } from '@/lib/form';
import { t, tn } from '@/lib/i18n';
import {
  lineOptions,
  linesCollection,
  type Network,
  pointsCollection,
  stationsCollection,
  useNetwork,
} from '@/lib/network';
import { useItem, useList, useSave } from '@/lib/resources';
import { nearestOnRoute } from '@/lib/route';
import type { Schedule, Station } from '@/lib/types';
import { travelOrdered } from '@/lib/zone-check';

export function StationsPage() {
  const network = useNetwork();
  const { can } = useAuth();
  const lineName = (id: string) => network.data?.lines.find((l) => l._id === id);
  return (
    <ResourceList<Station>
      title={t('Stations')}
      subtitle={t('Bus stops shown in the app. A station belongs to one or more directions.')}
      map={({ params, hovered, hover }) => (
        <StationsMap
          network={network.data}
          line={(params.lines as string) || ''}
          q={(params.q as string) || ''}
          hovered={hovered}
          hover={hover}
        />
      )}
      resource="stations"
      basePath="/stations"
      canCreate={can('admin')}
      newLabel={t('New station')}
      filters={(set, v) => (
        <Combobox
          className="w-64"
          value={(v.lines as string) ?? ''}
          onChange={(id) => set('lines', id)}
          emptyLabel={t('All directions')}
          placeholder={t('All directions')}
          options={lineOptions(network.data?.lines)}
        />
      )}
      columns={[
        { header: t('Name'), cell: (s) => <span className="font-medium">{s.name}</span> },
        {
          header: t('Directions'),
          cell: (s) => (
            <div className="flex flex-wrap gap-1.5">
              {s.lines.map((id) => {
                const l = lineName(id);
                return (
                  <span key={id} className="inline-flex items-center gap-1 text-xs text-slate-600">
                    <ColorDot color={l?.color} />
                    {l?.name ?? '—'}
                  </span>
                );
              })}
            </div>
          ),
        },
        {
          header: t('Coordinates'),
          cell: (s) => (
            <span className="font-mono text-xs text-slate-500">
              {s.geometry.coordinates.map((c) => c.toFixed(5)).join(', ')}
            </span>
          ),
        },
      ]}
    />
  );
}

/** Timetables of the directions that serve this station, with links to edit them. */
function StationTimetables({ stationId }: { stationId: string }) {
  const list = useList<Schedule>('schedules', { station: stationId, pageSize: 100 });
  const span = (times: string[]) =>
    times.length ? `${times.length} · ${times[0]}–${times.at(-1)}` : t('none');
  return (
    <Card title={t('Timetables at this station')} padded={false}>
      {list.data?.items.length ? (
        <ul className="divide-y divide-slate-100 text-sm">
          {list.data.items.map((s) => (
            <li key={s._id} className="px-4 py-2.5">
              <div className="flex items-baseline justify-between gap-2">
                <span className="truncate font-medium">{s.line?.name}</span>
                <Link
                  to={`/schedules/directions/${s.line?._id}`}
                  className="shrink-0 text-xs font-medium text-brand-700 hover:underline"
                >
                  {t('Edit timetable')}
                </Link>
              </div>
              <div className="mt-0.5 grid grid-cols-2 gap-2 text-xs text-slate-500">
                <span>
                  {t('Weekdays')}: {span([...s.week].sort())}
                </span>
                <span>
                  {t('Weekend')}: {span([...s.weekend].sort())}
                </span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="px-4 py-3 text-sm text-slate-500">{t('No timetables yet')}</p>
      )}
    </Card>
  );
}

type Net = Network;

/**
 * Directions that stop here, grouped by line: each line with its going and return direction as
 * toggles ("→ last stop"). Directions that belong to no line are listed after.
 */
function ServedDirections({
  network,
  stationId,
  value,
  onChange,
}: {
  network: Net;
  stationId?: string;
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const stationName = new Map(network.stations.map((s) => [s._id, s.name]));
  const byId = new Map(network.lines.map((l) => [l._id, l]));
  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  const inZone = new Set(network.zones.flatMap((z) => [z.on_line_going, z.on_line_return]).filter(Boolean));
  const others = network.lines.filter((l) => !inZone.has(l._id));
  const zones = [...network.zones]
    .filter((z) => z.on_line_going || z.on_line_return)
    .sort((a, b) => a.bus_sign.localeCompare(b.bus_sign, undefined, { numeric: true }));

  const chip = (lineId: string | undefined, label: string) => {
    const l = lineId ? byId.get(lineId) : undefined;
    if (!l) return null;
    const on = value.includes(l._id);
    const last = stationName.get(l.ordered_stations.at(-1) ?? '');
    const stop = stationId ? l.ordered_stations.indexOf(stationId) : -1;
    return (
      <button
        type="button"
        onClick={() => toggle(l._id)}
        aria-pressed={on}
        aria-label={l.name}
        title={l.name}
        className={`flex min-w-0 flex-1 items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-xs ring-1 transition ${
          on
            ? 'bg-brand-50 text-brand-900 ring-brand-300'
            : 'bg-white text-slate-600 ring-slate-200 hover:ring-slate-300'
        }`}
      >
        <span
          className={`flex size-4 shrink-0 items-center justify-center rounded border ${on ? 'border-brand-600 bg-brand-600 text-white' : 'border-slate-300'}`}
        >
          {on && <Check className="size-3" />}
        </span>
        <span className="min-w-0">
          <span className="block font-medium">{label}</span>
          <span className="block truncate text-slate-500">
            → {last ?? '—'}
            {on && stop >= 0 && ` · ${t('stop {n} of {m}', { n: stop + 1, m: l.ordered_stations.length })}`}
          </span>
        </span>
      </button>
    );
  };

  return (
    <div className="space-y-2">
      {zones.map((z) => {
        const c = safeHex(z.color);
        return (
          <div key={z._id} className="flex items-start gap-2">
            <span
              className="mt-1.5 inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1 text-[11px] font-bold"
              style={{ background: c, color: textOn(c) }}
              title={z.name}
            >
              {z.bus_sign}
            </span>
            <div className="flex min-w-0 flex-1 gap-1.5">
              {chip(z.on_line_going, t('Going'))}
              {chip(z.on_line_return, t('Return'))}
            </div>
          </div>
        );
      })}
      {others.length > 0 && (
        <div className="pt-1">
          <div className="mb-1 text-xs text-slate-500">{t('Directions without a line')}</div>
          <div className="flex flex-wrap gap-1.5">
            {others.map((l) => (
              <label key={l._id} className="flex items-center gap-1.5 text-xs text-slate-700">
                <input
                  type="checkbox"
                  checked={value.includes(l._id)}
                  onChange={() => toggle(l._id)}
                  className="rounded border-slate-300"
                />
                {l.name}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Stops further than this from a direction's route are flagged. */
const FAR_M = 50;

export function StationEditPage() {
  const { id } = useParams();
  const isNew = id === 'new';
  const item = useItem<Station>('stations', id);
  const network = useNetwork();
  const save = useSave<Station>('stations', id);
  const navigate = useNavigate();
  const { can } = useAuth();
  const readOnly = !can('admin');
  const info = useStationInfo(isNew ? undefined : id);
  const form = useFormState({
    name: '',
    description: '',
    lines: [] as string[],
    geometry: null as Station['geometry'] | null,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: load once per item
  useEffect(() => {
    if (item.data)
      form.setValues({
        name: item.data.name,
        description: item.data.description ?? '',
        lines: item.data.lines,
        geometry: item.data.geometry,
      });
  }, [item.data]);

  const net = network.data;
  const { values, set, errors } = form;
  const coords = values.geometry?.coordinates as [number, number] | undefined;
  const stationsById = useMemo(() => new Map(net?.stations.map((s) => [s._id, s])), [net]);
  // The directions this station is on: routes in travel order, coloured by their line.
  const served = useMemo(() => {
    if (!net) return [];
    return values.lines.flatMap((lid) => {
      const l = net.lines.find((x) => x._id === lid);
      if (!l) return [];
      const zone = net.zones.find((z) => z.on_line_going === lid || z.on_line_return === lid);
      const route = travelOrdered(l, stationsById);
      return [{ line: l, zone, color: safeHex(zone?.color ?? l.color), route }];
    });
  }, [net, values.lines, stationsById]);
  const routes = useMemo(
    () =>
      fc(
        served.map((s) => ({
          type: 'Feature' as const,
          geometry: { type: 'LineString' as const, coordinates: s.route },
          properties: { color: s.color, arrow: arrowIcon(s.color) },
        })),
      ),
    [served],
  );
  const far = coords
    ? served
        .map((s) => ({ name: s.line.name, m: Math.round(nearestOnRoute(s.route, coords).distance) }))
        .filter((x) => x.m > FAR_M)
    : [];

  if (!isNew && (item.isLoading || !net)) return <Spinner />;
  const servedZones = [...new Map(served.filter((s) => s.zone).map((s) => [s.zone!._id, s.zone!])).values()];

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        if (await form.submit(stationInput, (d) => save.mutateAsync(d))) navigate('/stations');
      }}
    >
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <img src={stationIcon()} alt="" className="size-7" />
            {isNew ? t('New station') : values.name || t('Station')}
          </span>
        }
        subtitle={
          servedZones.length ? (
            <span className="flex flex-wrap items-center gap-1.5">
              {t('Served by')}
              {servedZones.map((z) => {
                const c = safeHex(z.color);
                return (
                  <Link
                    key={z._id}
                    to={`/lines/${z._id}`}
                    className="inline-flex items-center gap-1 rounded-full py-0.5 pr-2 pl-0.5 text-xs ring-1 ring-slate-200 hover:ring-slate-400"
                  >
                    <span
                      className="inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold"
                      style={{ background: c, color: textOn(c) }}
                    >
                      {z.bus_sign}
                    </span>
                    {z.name}
                  </Link>
                );
              })}
            </span>
          ) : undefined
        }
        back="/stations"
      />
      <div className="grid items-start gap-6 lg:grid-cols-[420px_minmax(0,1fr)]">
        <div className="space-y-4">
          {readOnly && <ReadOnlyNotice />}
          <fieldset disabled={readOnly} className="min-w-0 space-y-4">
            <Card title={t('Details')}>
              <div className="space-y-4">
                <Field label={t('Name')} error={errors.name}>
                  {(fid) => (
                    <Input
                      id={fid}
                      value={values.name}
                      onChange={(e) => set('name', e.target.value)}
                      invalid={!!errors.name}
                    />
                  )}
                </Field>
                <Field label={t('Description')} error={errors.description}>
                  {(fid) => (
                    <Textarea
                      id={fid}
                      rows={2}
                      value={values.description}
                      onChange={(e) => set('description', e.target.value)}
                    />
                  )}
                </Field>
              </div>
            </Card>

            <Card title={t('Directions that stop here ({n})', { n: values.lines.length })}>
              {net && (
                <ServedDirections
                  network={net}
                  stationId={isNew ? undefined : id}
                  value={values.lines}
                  onChange={(v) => set('lines', v)}
                />
              )}
              {errors.lines && <p className="mt-2 text-sm text-red-600">{errors.lines}</p>}
              <p className="mt-3 text-xs text-slate-500">
                {t(
                  "Adding a direction places this station by its position on the route; reorder stations on the direction's page.",
                )}
              </p>
            </Card>

            <Card title={t('Position')}>
              {coords ? (
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs text-slate-600">
                      {coords[1].toFixed(6)}, {coords[0].toFixed(6)}
                    </span>
                    <a
                      href={`https://www.google.com/maps?q=${coords[1]},${coords[0]}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs font-medium text-brand-700 hover:underline"
                    >
                      {t('Open in Google Maps')}
                    </a>
                  </div>
                  <p className="text-xs text-slate-500">
                    {t('Drag the sign on the map, or click where the stop is.')}
                  </p>
                  {far.map((f) => (
                    <p key={f.name} className="flex items-center gap-1.5 text-xs text-amber-700">
                      <TriangleAlert className="size-3.5 shrink-0" />
                      {t('{m} m from the route of {direction}', { m: f.m, direction: f.name })}
                    </p>
                  ))}
                </div>
              ) : (
                <p className={`text-sm ${errors.geometry ? 'text-red-600' : 'text-slate-500'}`}>
                  {t('Click on the map where the stop is.')}
                </p>
              )}
            </Card>
          </fieldset>

          {!isNew && id && <StationTimetables stationId={id} />}

          {!isNew && info.data && (
            <Card title={t('Arriving within 30 minutes')}>
              <div className="text-sm">
                <StationArrivals incoming={info.data.incoming} />
              </div>
            </Card>
          )}

          <div className="sticky bottom-0 z-10 rounded-lg bg-white/95 px-4 pb-4 shadow-sm ring-1 ring-slate-200 backdrop-blur">
            <FormActions
              saving={save.isPending}
              resource="stations"
              id={id}
              backTo="/stations"
              canDelete={can('admin')}
              canSave={can('admin')}
            />
          </div>
        </div>
        <div className="lg:sticky lg:top-6">
          <MapView className="h-[70vh] lg:h-[calc(100vh-9rem)]" center={coords} zoom={coords ? 16 : 12.5}>
            <GeoJsonLayer
              data={routes}
              layers={[lineLayer(['get', 'color'], 4, 0.85), arrowLayer(['get', 'arrow'] as never)]}
            />
            <StationsLayer
              stations={(net?.stations ?? []).filter((s) => s._id !== id)}
              minzoom={0}
              dim={0.6}
            />
            <GeoJsonLayer
              data={stationsCollection(net?.stations, id)}
              layers={[{ ...labelLayer(), minzoom: 15 }]}
            />
            <PointPicker
              readOnly={readOnly}
              value={coords}
              icon={stationIcon()}
              onChange={(p) => set('geometry', { type: 'Point', coordinates: p })}
            />
          </MapView>
        </div>
      </div>
    </form>
  );
}

const SHOW_LINES_KEY = 'trafiku_stations_show_lines';

/** Stations matching the list's search and line filter; lines on request. */
function StationsMap({
  network,
  line,
  q,
  hovered,
  hover,
}: {
  network?: Network;
  line: string;
  q: string;
  hovered: string | null;
  hover: (id: string | null) => void;
}) {
  const [showLines, setShowLines] = useState(() => {
    try {
      return localStorage.getItem(SHOW_LINES_KEY) === '1';
    } catch {
      return false;
    }
  });
  const toggle = (on: boolean) => {
    setShowLines(on);
    try {
      localStorage.setItem(SHOW_LINES_KEY, on ? '1' : '0');
    } catch {}
  };
  const selectedLine = network?.lines.find((l) => l._id === line);
  const stations = useMemo(() => {
    const words = fold(q).split(/\s+/).filter(Boolean);
    return (network?.stations ?? []).filter(
      (s) => (!line || s.lines.includes(line)) && words.every((w) => fold(s.name).includes(w)),
    );
  }, [network, line, q]);
  const data = useMemo(
    () =>
      pointsCollection(
        stations,
        (s) => s.geometry.coordinates as [number, number],
        (s) => s.name,
        safeHex(selectedLine?.color, '#0b3f82'),
      ),
    [stations, selectedLine],
  );
  const lines = useMemo(
    () => (showLines ? linesCollection(network?.lines, line ? (id) => id === line : undefined) : fc([])),
    [network, showLines, line],
  );
  return (
    <OverviewMap
      data={data}
      kind="station"
      hovered={hovered}
      onHover={hover}
      basePath="/stations"
      fitKey={`${line}|${q}`}
      overlay={
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500">{tn(stations.length, '{n} station', '{n} stations')}</span>
          <label className="flex cursor-pointer items-center gap-1.5 text-xs font-medium text-slate-700">
            <input
              type="checkbox"
              checked={showLines}
              onChange={(e) => toggle(e.target.checked)}
              className="rounded border-slate-300"
            />
            {line ? t('Show the direction') : t('Show directions')}
          </label>
        </div>
      }
    >
      {/* Always mounted (empty when off) so the lines stay underneath the stations. */}
      <GeoJsonLayer data={lines} layers={[lineLayer(undefined, line ? 5 : 3, line ? 0.8 : 0.5)]} />
    </OverviewMap>
  );
}

let stationIconUrl: string | undefined;
/** The highlighted station sign, for the draggable marker of the station being edited. */
const stationIcon = () => (stationIconUrl ??= iconUrl(drawStation(true)));
